
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';

// --- Models ---
import { DriverType } from '@/models/Driver.model';

// --- Services ---
import AppService from '@/services/app';
import DriverTypeService from '@/services/driverType';

@Component
export default class DriverTypeListing extends Vue {

  // used by No Data Slot
  isLoading: boolean = true;

  // used by Item Dialog for Create/Update
  showItemDialog: boolean = false;
  valid: boolean = false;
  title: string = '';
  clickedItem: DriverType = {
    id: 0,
    type: '',
    rateType: '',
    rate: 0,
    driverCount: 0,
  };
  rateString: string = '';

  clicked: boolean = false;

  // used by Confirm Action Dialog for Delete
  action: string = '';
  dialogContent: string = '';
  showConfirmActionDialog: boolean = false;
  confirmActionDelay: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
    requiredRateString: (value: string) => !!value && parseFloat(value) > 0 || '',
    currency: (value: string) =>  /^\d{0,8}(?:\.\d{1,2})?$/.test(value) || 'Limited to two decimal places',
    maxDriverRateValue: (value: number | string) => {
      if ((typeof value) === 'string') {
        return !!value && parseFloat('' + value) < 100000 || '';
      } else {
        return value < 100000 || '';
      }
    },
  };

  // #region Functions to display list data on page load
    headers() {
      return [
        {
          text: 'Type',
          value: 'type',
        },
        {
          text: 'Rate',
          value: 'rate',
        },
        {
          text: 'Driver Count',
          value: 'driverCount',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    async beforeMount() {
      await this.listAll();
    }

    async listAll() {
      try {
        this.isLoading = true;
        const response: string = await DriverTypeService.listAll(true);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    allowAddNewFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }

    allowDeleteFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
      ]);
    }
  // #endregion

  // #region Functions required for the Item Dialog for Create/Update
    async launchNewItemDialog(item: DriverType) {
      this.clickedItem = {
        id: 0,
        type: '',
        rateType: '',
        rate: 0,
        driverCount: 0,
      };

      this.rateString = this.clickedItem.rate.toFixed(2);

      this.valid = false;
      this.title = 'Create New Driver Type';

      this.showItemDialog = true;
    }

    async launchUpdateItemDialog(item: DriverType) {
      this.clickedItem = Object.assign({}, item);
      this.rateString = this.clickedItem.rate.toFixed(2);

      this.valid = true;
      this.title = 'Update Driver Type';
      this.showItemDialog = true;
    }
    
    doCreateUpdate() {
      if (this.clickedItem.id <= 0) {
        this.doCreate();
      } else {
        this.doUpdate();
      }
    }

    async doCreate() {
      try {
        this.clicked = true;

        const response = await DriverTypeService.create({
          type: this.clickedItem.type,
          rate: parseFloat(this.rateString),
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverTypes.vue - doCreate - 407 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

    async doUpdate() {
      try {
        this.clicked = true;

        const response = await DriverTypeService.update({
          id: this.clickedItem.id,
          type: this.clickedItem.type,
          rate: parseFloat(this.rateString),
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverTypes.vue - doUpdate - 443 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Delete
    async launchDeleteItemDialog(item: DriverType) {
      this.clickedItem = Object.assign({}, item);

      this.action = 'DELETE';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #C01212">delete</span> ' 
        + 'the ' + this.clickedItem.type + ' Driver Type';
      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'DELETE') {
        await this.doDelete();
      }
    }

    async doDelete() {
      try {
        this.confirmActionDelay = true;

        const response = await DriverTypeService.delete({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverTypes.vue - doDelete - 498 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }
  // #endregion
}
